import React from "react";
import { getCalenderVibe } from "../api/vibe/index";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import DashboardHeader from "../components/Other/DashboardHeader";
import PageContainer from "../components/pages/PageContainer";
// import PageHeader from "../components/PageHeader";
import { useTranslation } from "react-i18next";
import CustomPageHeader from "../components/pages/CustomPageHeader";
import VibeCard from "../components/MyVibe/VibeCard";
import Loader from "../components/Loader";

const CalendarPage = () => {
  const { t } = useTranslation();
  // const [month, setMonth] = React.useState(7); // August (0-indexed)
  // const [year, setYear] = React.useState(2025);
  const today = new Date();
  const [month, setMonth] = React.useState(today.getMonth()); // 0-indexed, so 8 = September
  const [year, setYear] = React.useState(today.getFullYear());

  const [selectedDate, setSelectedDate] = React.useState(null);

  // Separate states
  const [calendarVibes, setCalendarVibes] = React.useState([]);

  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [lastPage, setLastPage] = React.useState(null);
  const requestInProgress = React.useRef(false);
  const observerRef = React.useRef(null);
  const loaderRef = React.useRef(null);

  // Pagination state for selected date
  const [selectedPage, setSelectedPage] = React.useState(1);
  const vibesPerPage = 10;

  const monthNames = [
    t("calendar_january", "January"),
    t("calendar_february", "February"),
    t("calendar_march", "March"),
    t("calendar_april", "April"),
    t("calendar_may", "May"),
    t("calendar_june", "June"),
    t("calendar_july", "July"),
    t("calendar_august", "August"),
    t("calendar_september", "September"),
    t("calendar_october", "October"),
    t("calendar_november", "November"),
    t("calendar_december", "December"),
  ];

  // --- Fetch Calendar Vibes (monthly with pagination) ---
  React.useEffect(() => {
    if (selectedDate) return; // skip when showing daily
    if (requestInProgress.current) return;
    requestInProgress.current = true;
    setLoading(true);

    const fetchCalendarVibes = async () => {
      try {
        const monthStr = `${year}-${String(month + 1).padStart(2, "0")}-01`;
        const res = await getCalenderVibe({ date: monthStr, page, limit: 50 });

        const newVibes = Array.isArray(res?.data?.data) ? res.data.data : [];
        const apiLastPage = Number(res?.data?.last_page || 1);

        setCalendarVibes((prev) => {
          if (page === 1) return newVibes;
          const ids = new Set(prev.map((v) => v.id));
          return [...prev, ...newVibes.filter((v) => !ids.has(v.id))];
        });
        setLastPage(apiLastPage);
      } catch (err) {
        if (page === 1) setCalendarVibes([]);
      } finally {
        setLoading(false);
        requestInProgress.current = false;
      }
    };

    fetchCalendarVibes();
  }, [month, year, page, selectedDate]);

  // Reset pagination + vibes when month/year changes
  React.useEffect(() => {
    setPage(1);
    setLastPage(null);
    setCalendarVibes([]); // 🔑 Clear previous month vibes
  }, [month, year]);

  // Infinite scroll (only for monthly view)
  React.useEffect(() => {
    if (!loaderRef.current || selectedDate) return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const noMorePages = lastPage !== null && page >= lastPage;
        if (
          entry.isIntersecting &&
          !loading &&
          !requestInProgress.current &&
          !noMorePages
        ) {
          setPage((prev) => prev + 1);
        }
      },
      { root: null, rootMargin: "200px", threshold: 0 }
    );

    observerRef.current.observe(loaderRef.current);
    return () => observerRef.current && observerRef.current.disconnect();
  }, [loading, lastPage, page, selectedDate]);

  // Calendar build helpers
  const getDaysInMonth = (year, month) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfWeek = (year, month) => new Date(year, month, 1).getDay();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const prevMonthDays = getDaysInMonth(year, month === 0 ? 11 : month - 1);

  // Build calendar grid
  let calendar = [];
  let dayNum = 1;
  let nextMonthDay = 1;
  for (let week = 0; week < 6; week++) {
    let weekRow = [];
    for (let day = 0; day < 7; day++) {
      let cell = null;
      if (week === 0 && day < firstDay) {
        // Generate proper date for previous month
        const prevMonth = month === 0 ? 11 : month - 1;
        const prevYear = month === 0 ? year - 1 : year;
        const dayLabel = prevMonthDays - firstDay + day + 1;
        cell = {
          date: `${prevYear}-${String(prevMonth + 1).padStart(2, "0")}-${String(dayLabel).padStart(2, "0")}`,
          label: dayLabel,
          isOtherMonth: true,
        };
      } else if (dayNum > daysInMonth) {
        // Generate proper date for next month
        const nextMonth = month === 11 ? 0 : month + 1;
        const nextYear = month === 11 ? year + 1 : year;
        cell = { 
          date: `${nextYear}-${String(nextMonth + 1).padStart(2, "0")}-${String(nextMonthDay).padStart(2, "0")}`, 
          label: nextMonthDay++, 
          isOtherMonth: true 
        };
      } else {
        cell = {
          date: `${year}-${String(month + 1).padStart(2, "0")}-${String(
            dayNum
          ).padStart(2, "0")}`,
          label: dayNum,
          isOtherMonth: false,
        };
        dayNum++;
      }
      weekRow.push(cell);
    }
    calendar.push(weekRow);
    if (dayNum > daysInMonth && nextMonthDay > 7) break;
  }

  // Reset selectedPage when selectedDate or month changes
  React.useEffect(() => {
    setSelectedPage(1);
  }, [selectedDate, month, year]);

  // Filter calendar vibes for current month
  const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;
  const monthVibes = calendarVibes.filter((v) => v.date.startsWith(monthStr));

  // Show vibes for selected date or all calendar vibes for month
  let showVibes = [];
  if (selectedDate) {
    const filtered = calendarVibes.filter((v) => v.date === selectedDate);
    showVibes = filtered.slice(
      (selectedPage - 1) * vibesPerPage,
      selectedPage * vibesPerPage
    );
  } else {
    showVibes = monthVibes;
  }

  // Pagination controls for selected date
  const selectedTotalPages = selectedDate
    ? Math.ceil(
        monthVibes.filter((v) => v.date === selectedDate).length / vibesPerPage
      )
    : 1;

  const handlePrevMonth = () => {
    setSelectedDate(null);
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };
  const handleNextMonth = () => {
    setSelectedDate(null);
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  return (
    <>
      <CustomPageHeader
        hideBack={true}
        showPageHeader={false}
        pageContainerClass=""
        title={t("calendar", "Vibelinks")}
      >
        <div className="">
          {/* Calendar Header */}
          <div className="w-full flex justify-between items-center pt-5 pb-2">
            <span className=" lg:block  text-xl sm:text-2xl md:text-3xl lg:text-[2.1rem] font-extrabold text-gray-900 dark:text-dark-text tracking-tight">
              {t("calendar")}
            </span>
            <span className="text-[#6d29f6] font-semibold text-xs sm:text-base cursor-pointer">
              <Link
                to="/create-vibe"
                state={selectedDate ? { selectedDate } : undefined}
              >
                {t("add_new_vibe")}
              </Link>
            </span>
          </div>

          {/* Calendar Table */}
          <div className="bg-transparent rounded-2xl  pt-4 pb-2 w-full  mb-4">
            <div className="flex rounded-xl items-center justify-between mb-3 gap-2 bg-[#6d29f6]">
              <button
                className="text-white rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold shadow transition duration-150"
                onClick={handlePrevMonth}
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <span className="text-white rounded-xl px-7 py-2 font-bold text-lg min-w-[140px] text-center">
                {monthNames[month]} {year}
              </span>
              <button
                className="text-white rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold shadow transition duration-150"
                onClick={handleNextMonth}
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>

            <table className="w-full border-collapse mb-1 text-center">
              <thead>
                <tr className="text-gray-400 dark:text-gray-300 font-semibold text-sm md:text-base">
                  <th>{t("calendar_sunday", "S")}</th>
                  <th>{t("calendar_monday", "M")}</th>
                  <th>{t("calendar_tuesday", "T")}</th>
                  <th>{t("calendar_wednesday", "W")}</th>
                  <th>{t("calendar_thursday", "T")}</th>
                  <th>{t("calendar_friday", "F")}</th>
                  <th>{t("calendar_saturday", "S")}</th>
                </tr>
              </thead>
              <tbody className="text-[10px] sm:text-xs md:text-sm lg:text-base text-gray-900 dark:text-dark-text font-medium">
                {calendar.map((week, wIdx) => (
                  <tr key={wIdx}>
                    {week.map((cell, dIdx) => (
                      <td
                        key={dIdx}
                        className={`relative cursor-pointer select-none px-1 py-2 sm:px-1.5 sm:py-2.5 lg:px-2 lg:py-3 ${
                          cell.isOtherMonth ? "text-gray-500 dark:text-gray-400" : ""
                        } ${
                          cell.date === selectedDate
                            ? "rounded-md font-bold"
                            : ""
                        }`}
                        onClick={() => cell.date && setSelectedDate(cell.date)}
                      >
                        <span
                          className={
                            (cell.date === selectedDate && !cell.isOtherMonth
                              ? "bg-[#6d29f6] text-white rounded-full font-bold shadow flex items-center justify-center"
                              : !cell.isOtherMonth &&
                                cell.date &&
                                calendarVibes.some((v) => v.date === cell.date)
                              ? "bg-[#6d29f6] text-white rounded-md font-bold shadow flex items-center justify-center"
                              : "flex items-center justify-center") +
                            " w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9 text-[10px] sm:text-sm md:text-base mx-auto"
                          }
                        >
                          {cell.label}
                        </span>
                        {cell.date &&
                          !cell.isOtherMonth &&
                          calendarVibes.some((v) => v.date === cell.date) &&
                          (() => {
                            const vibe = calendarVibes.find(
                              (v) => v.date === cell.date
                            );
                            return (
                              <div className="absolute left-1/2 -translate-x-1/2 top-7 sm:top-8 md:top-9 lg:top-10 flex items-center justify-center gap-0.5 sm:gap-1">
                                {vibe.green_flag > 0 && (
                                  <span className="bg-[#60c6b9] text-white rounded-full w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-[7px] sm:text-[9px] md:text-xs flex items-center justify-center font-bold border-2 border-white">
                                    {vibe.green_flag}
                                  </span>
                                )}
                                {vibe.red_flag > 0 && (
                                  <span className="bg-[#e4595a] text-white rounded-full w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-[7px] sm:text-[9px] md:text-xs flex items-center justify-center font-bold border-2 border-white">
                                    {vibe.red_flag}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Vibes List */}
          <div className="w-full  dark:bg-[#252739] px-2 py-2">

            {loading && page === 1 ? (
              <div className="bg-white dark:bg-[#252739] rounded-2xl shadow-md flex items-center justify-center mb-4 px-4 py-8 text-gray-400 dark:text-dark-text-secondary font-semibold text-lg">
                {t("calendar_loading", "Loading...")}
              </div>
            ) : showVibes.length > 0 ? (
              <>
                {showVibes.map((vibe, idx) => {

                  const [year, month, day] = vibe.date.split("-");

                  return (
                    <Link to={`/vibe/${vibe.id}`} key={idx} className="block">
                      <div className="mb-2">
                        <VibeCard vibe={vibe} />
                      </div>
                    </Link>
                  );
                })}

                {/* Loader for infinite scroll */}
                {loading && page > 1 && <Loader />}
                {/* {lastPage !== null && page >= lastPage && (
                  <div className='text-center text-gray-400 py-2 text-sm'>
                    No more vibes
                  </div>
                )} */}
                <div ref={loaderRef} className="h-1" />
              </>
            ) : (
              <div className="bg-white dark:bg-[#252739] rounded-2xl shadow-md flex items-center justify-center mb-4 px-4 py-8 text-gray-400 dark:text-dark-text-secondary font-semibold text-lg">
                {selectedDate
                  ? t("calendar_no_vibes_date", "No vibes for this date.")
                  : t("calendar_no_vibes_month", "No vibes for this month.")}
              </div>
            )}

            {/* Pagination controls for selected date */}
            {selectedDate && selectedTotalPages > 1 && (
              <div className="flex justify-center items-center gap-2 my-4">
                <button
                  className="px-3 py-1 rounded bg-gray-200 dark:bg-[#252739] text-gray-700 dark:text-dark-text font-bold disabled:opacity-50"
                  onClick={() => setSelectedPage((p) => Math.max(1, p - 1))}
                  disabled={selectedPage === 1}
                >
                  {t("calendar_prev", "Prev")}
                </button>
                <span className="px-2 font-bold text-gray-900 dark:text-dark-text">
                  {t("calendar_page", "Page")} {selectedPage}{" "}
                  {t("calendar_of", "of")} {selectedTotalPages}
                </span>
                <button
                  className="px-3 py-1 rounded bg-gray-200 dark:bg-[#252739] text-gray-700 dark:text-dark-text font-bold disabled:opacity-50"
                  onClick={() =>
                    setSelectedPage((p) => Math.min(selectedTotalPages, p + 1))
                  }
                  disabled={selectedPage === selectedTotalPages}
                >
                  {t("calendar_next", "Next")}
                </button>
              </div>
            )}
          </div>
        </div>
      </CustomPageHeader>
    </>
  );
};

export default CalendarPage;
